import * as React from "react"
import { ChevronDown, Maximize2, MoveHorizontal } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"

// Carries, to descendants: whether they're rendered inside the expanded pop-out
// modal (`expanded`) and a `setOpen` to open/close it. Scoped per Expandable
// instance, so the same children can render at both `expanded` values
// simultaneously (inline vs modal).
const ExpandedContext = React.createContext({
  expanded: false,
  open: false,
  setOpen: () => {},
  inlineExpanded: false,
  setInlineExpanded: () => {},
})

function useExpanded() {
  return React.useContext(ExpandedContext).expanded
}

function useExpandable() {
  return React.useContext(ExpandedContext)
}

// Wraps a card/chart/table with a maximize button that pops the same content
// into a large modal. The whole subtree re-renders inside the modal with
// `expanded` === true, so children can render fuller (more rows, taller charts).
//
// - `showHeader`: render a visible modal header (title/description). Turn off
//   when the popped content already carries its own title (e.g. a ChartCard).
// - `showTrigger`: render the built-in top-right maximize button. Turn off and
//   drive `open`/`onOpenChange` yourself to place the button elsewhere.
// - `modalLayout`: "scroll" (default) – the whole popped subtree scrolls as one
//   block (charts). "fit" – the modal body is a fixed flex column and the
//   content manages its own scroll region, so toolbars/controls stay pinned
//   while an inner ExpandableScroll scrolls (tables). Pair with <ExpandableFill>.
function Expandable({
  id,
  title,
  description,
  children,
  className,
  showHeader = true,
  showTrigger = true,
  triggerClassName,
  modalLayout = "scroll",
  open: controlledOpen,
  onOpenChange,
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false)
  const [inlineExpanded, setInlineExpanded] = React.useState(false)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : uncontrolledOpen
  const setOpen = React.useCallback(
    (next) => {
      if (!isControlled) setUncontrolledOpen(next)
      if (onOpenChange) onOpenChange(next)
    },
    [isControlled, onOpenChange]
  )
  const label = title ? `Expand ${title}` : "Expand"

  const collapsedValue = React.useMemo(
    () => ({ expanded: false, open, setOpen, inlineExpanded, setInlineExpanded }),
    [open, setOpen, inlineExpanded],
  )
  const expandedValue = React.useMemo(
    () => ({ expanded: true, open, setOpen, inlineExpanded: true, setInlineExpanded }),
    [open, setOpen],
  )

  // The popped-out content can be heavy (a full page of table rows, each with
  // many inputs). Mounting it in the same commit that opens the dialog blocks the
  // click until everything renders, which feels like a freeze. Instead, paint the
  // dialog shell first and mount the content on the next frame so opening is
  // instant; a light placeholder holds the space for that one frame.
  const [contentMounted, setContentMounted] = React.useState(false)
  React.useEffect(() => {
    if (!open) {
      setContentMounted(false)
      return undefined
    }
    const frame = requestAnimationFrame(() => setContentMounted(true))
    return () => cancelAnimationFrame(frame)
  }, [open])

  return (
    <div id={id} className={cn("relative min-w-0", className)}>
      <ExpandedContext.Provider value={collapsedValue}>{children}</ExpandedContext.Provider>

      {showTrigger && (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={() => setOpen(true)}
          aria-label={label}
          title={label}
          className={cn(
            "absolute right-2 top-2 z-20 bg-background/85 text-muted-foreground shadow-sm ring-1 ring-border/70 backdrop-blur-sm hover:text-foreground",
            triggerClassName
          )}
        >
          <Maximize2 className="size-4" />
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex h-[92vh] w-[95vw] max-w-[95vw] flex-col gap-3">
          {showHeader ? (
            <DialogHeader>
              <DialogTitle>{title}</DialogTitle>
              {description && <DialogDescription>{description}</DialogDescription>}
            </DialogHeader>
          ) : (
            <DialogTitle className="sr-only">{title}</DialogTitle>
          )}
          {/* "scroll": one scroll container holding everything (charts).
              "fit": a fixed flex column; the content's own ExpandableScroll
              scrolls while toolbars above it stay pinned (tables). */}
          <div
            className={
              modalLayout === "fit"
                ? "flex min-h-0 min-w-0 flex-1 flex-col gap-2"
                : "min-h-0 min-w-0 flex-1 overflow-auto"
            }
          >
            {open && (
              <ExpandedContext.Provider value={expandedValue}>
                {contentMounted ? children : (
                  <div className="flex min-h-40 flex-1 items-center justify-center text-sm text-muted-foreground">
                    Loading…
                  </div>
                )}
              </ExpandedContext.Provider>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// Horizontal-scroll cue meant to sit beside a table's summary/toolbar text
// rather than take its own row. `when` picks which context it shows in:
// "inline" (default) only on the normal pane, "expanded" only in the pop-out
// modal, "both" everywhere – useful because the inline pane and the modal often
// have room on different rows.
function ScrollHint({ className, when = "inline", children = "Scroll horizontally to see all columns" }) {
  const expanded = useExpanded()
  if (when === "inline" && expanded) return null
  if (when === "expanded" && !expanded) return null
  return (
    <span
      className={cn(
        "flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground",
        className
      )}
    >
      <MoveHorizontal className="size-3.5 shrink-0" />
      {children}
    </span>
  )
}

// Scroll container that swaps its classes depending on whether it's inline or
// inside the expanded modal. Inline, a table only scrolls horizontally so the
// surrounding pane owns vertical scrolling (no nested-scroll trap); in the
// modal, the Expandable body owns scrolling so this just grows to content.
const ExpandableScroll = React.forwardRef(function ExpandableScroll(
  { className, collapsedClassName, expandedClassName, ...props },
  ref
) {
  const expanded = useExpanded()
  return (
    <div
      // Children render twice while expanded (inline + modal); bind the
      // forwarded ref to the inline instance only so the modal copy doesn't
      // steal it (e.g. scroll-into-view targets).
      ref={expanded ? null : ref}
      className={cn(className, expanded ? expandedClassName : collapsedClassName)}
      {...props}
    />
  )
})

// A passthrough container that becomes a vertical flex-fill region only inside
// the "fit" modal – letting an inner ExpandableScroll claim the leftover height
// and scroll. Inline (and in "scroll" modals) it's just a plain div, so it has
// no effect on the normal layout.
function ExpandableFill({ className, children, ...props }) {
  const expanded = useExpanded()
  return (
    <div className={cn(className, expanded && "flex min-h-0 flex-1 flex-col")} {...props}>
      {children}
    </div>
  )
}

// Limits how many row elements render inline; shows all of them in the modal.
// Place inside a <tbody> around a rows .map(). Pair with <ShowMoreBar> for the
// "more hidden" affordance.
const ROW_LIMIT = 5

function RowLimit({ limit = ROW_LIMIT, children }) {
  const { expanded, inlineExpanded } = React.useContext(ExpandedContext)
  if (expanded || inlineExpanded) return <>{children}</>
  return <>{React.Children.toArray(children).slice(0, limit)}</>
}

// Context-aware rows for a table that paginates only in the pop-out. Inline it
// renders the first `limit` rows of the whole filtered set (`inlineRows`) – a
// stable preview that never gets stuck on an out-of-range page – while the
// expanded pop-out renders `pagedRows` (the current page). `renderRow(row)`
// must return an element with a key. Use instead of <RowLimit> when inline must
// ignore pagination.
function PaginatedRows({
  inlineRows,
  pagedRows,
  renderRow,
  limit = ROW_LIMIT,
  showAllInline = false,
  initialExpandedRows = null,
  expandedRenderChunkSize = 0,
  priorityExpandedIndex = null,
}) {
  const { expanded, inlineExpanded } = React.useContext(ExpandedContext)
  const expandedRows = React.useMemo(() => (
    Array.isArray(pagedRows) ? pagedRows : []
  ), [pagedRows])
  const staged = expanded
    && Number.isFinite(initialExpandedRows)
    && initialExpandedRows > 0
    && expandedRows.length > initialExpandedRows
  const priorityCount = Number.isInteger(priorityExpandedIndex) && priorityExpandedIndex >= 0
    ? priorityExpandedIndex + 1
    : 0
  const firstExpandedCount = staged
    ? Math.min(expandedRows.length, Math.max(initialExpandedRows, priorityCount))
    : expandedRows.length
  const [stagedRows, setStagedRows] = React.useState({ rows: null, count: firstExpandedCount })
  const expandedCount = staged
    ? Math.min(expandedRows.length, stagedRows.rows === expandedRows ? stagedRows.count : firstExpandedCount)
    : expandedRows.length

  React.useEffect(() => {
    if (!expanded || !staged) {
      setStagedRows((prev) => (
        prev.rows === expandedRows && prev.count === expandedRows.length
          ? prev
          : { rows: expandedRows, count: expandedRows.length }
      ))
      return undefined
    }

    let cancelled = false
    let frame = null
    let count = firstExpandedCount
    const chunkSize = Math.max(1, Number(expandedRenderChunkSize) || expandedRows.length)
    setStagedRows({ rows: expandedRows, count })

    const step = () => {
      if (cancelled) return
      count = Math.min(expandedRows.length, count + chunkSize)
      setStagedRows({ rows: expandedRows, count })
      if (count < expandedRows.length) frame = requestAnimationFrame(step)
    }

    if (count < expandedRows.length) frame = requestAnimationFrame(step)
    return () => {
      cancelled = true
      if (frame !== null) cancelAnimationFrame(frame)
    }
  }, [expanded, staged, expandedRows, firstExpandedCount, expandedRenderChunkSize])

  const rows = expanded
    ? expandedRows.slice(0, expandedCount)
    : inlineRows.slice(0, inlineExpanded || showAllInline ? inlineRows.length : limit)
  return <>{rows.map(renderRow)}</>
}

// Full-width bar shown below a RowLimit-capped table (place it OUTSIDE the
// horizontally-scrolling box so it spans the visible width and never scrolls
// away). `total` is the number of rows in the current view; it hides itself in
// the modal and when nothing is hidden.
function ShowMoreBar({ total, limit = ROW_LIMIT }) {
  const { expanded, inlineExpanded, setOpen } = React.useContext(ExpandedContext)
  const hidden = total - limit
  if (expanded || inlineExpanded || hidden <= 0) return null
  return (
    <button
      type="button"
      onClick={() => setOpen(true)}
      aria-label={`Open the full table with ${total} rows`}
      className="group/more flex w-full items-center gap-1.5 rounded-b-md border-x border-b border-border bg-primary/[0.06] px-3 py-2 text-left text-xs font-medium text-primary transition-colors hover:bg-primary/10"
    >
      <ChevronDown className="size-3.5 shrink-0 transition-transform group-hover/more:translate-y-0.5" />
      {hidden} more hidden – open the full table
    </button>
  )
}

export { Expandable, ExpandableScroll, ExpandableFill, RowLimit, PaginatedRows, ShowMoreBar, ScrollHint, ROW_LIMIT, ExpandedContext, useExpanded, useExpandable }
